import { useState, useRef, useEffect } from "react"
import { useNavigate, Link }            from "react-router-dom"
import { api, setToken }                from "@/lib/api-client"
import { useAuth }                      from "@/hooks/useAuth"

function ChevronIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  )
}

export default function TenantSwitcher() {
  const { user, refresh } = useAuth()
  const [open, setOpen]   = useState(false)
  const [busy, setBusy]   = useState<string | null>(null)
  const ref               = useRef<HTMLDivElement>(null)
  const navigate          = useNavigate()

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener("mousedown", onClick)
    return () => document.removeEventListener("mousedown", onClick)
  }, [open])

  if (!user) return null

  async function handleSwitch(id: string) {
    if (id === user?.tenantId) { setOpen(false); return }
    setBusy(id)
    try {
      const { token } = await api.tenants.switch(id)
      setToken(token)
      await refresh()
      setOpen(false)
      navigate("/app/dashboard")
    } finally {
      setBusy(null)
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900 px-1.5 py-1 rounded hover:bg-zinc-50"
      >
        <span className="truncate max-w-[180px]">{user.tenantName}</span>
        <ChevronIcon />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 min-w-[240px] bg-white border border-zinc-200 rounded-lg shadow-lg z-20 py-1">
          <div className="px-3 py-1.5 text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">
            Workspaces
          </div>
          {(user.tenants ?? []).map(t => {
            const isCurrent = t.id === user.tenantId
            return (
              <button
                key={t.id}
                disabled={busy !== null}
                onClick={() => handleSwitch(t.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left hover:bg-zinc-50 ${
                  isCurrent ? "bg-zinc-50" : ""
                } disabled:opacity-40`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${isCurrent ? "bg-zinc-900" : "bg-zinc-200"}`} />
                <span className="flex-1 truncate">{t.name}</span>
                {t.subscriptionStatus === "expired" && (
                  <span className="text-[10px] px-1 py-0.5 rounded text-red-700 bg-red-50 border border-red-200">expired</span>
                )}
                {t.subscriptionStatus === "comped" && (
                  <span className="text-[10px] px-1 py-0.5 rounded text-violet-700 bg-violet-50 border border-violet-200">comped</span>
                )}
              </button>
            )
          })}
          <div className="border-t border-zinc-100 mt-1 pt-1">
            <Link
              to="/app/tenants/new"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-[13px] text-zinc-600 hover:bg-zinc-50"
            >
              <span className="text-lg leading-none">+</span>
              New workspace
            </Link>
            <Link
              to="/app/tenants"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-[13px] text-zinc-500 hover:bg-zinc-50"
            >
              Manage workspaces
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
